import { SelectionModel } from "@angular/cdk/collections";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { Router } from "@angular/router";
import { Choice } from "@comunicare/comunicare-angular-filters-display";
import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  Appointment,
  DataType,
  FORMS_MODE,
  IAppointment,
  IFilter,
  ITableParameter,
  PreferenceContext,
  TELECONSULTATION_PERIOD_KEY,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Observable, Subject, forkJoin, merge } from "rxjs";
import { filter, first, skipWhile, takeUntil, tap } from "rxjs/operators";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PatientTeleconsultationDialogComponent } from "src/app/components/patient-teleconsultations-dialog/patient-teleconsultations-dialog.component";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { AppointmentService } from "src/app/providers/appointment.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientService } from "src/app/providers/patient.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { FEATURES } from "src/environments/features";
import { TeleconsultationDataSource } from "./teleconsultation-list-datasource";

@Component({
  selector: "app-teleconsultation-page",
  templateUrl: "./teleconsultation-page.component.html",
  styleUrls: ["./teleconsultation-page.component.scss"],
})
export class TeleconsultationsPageComponent implements OnInit, OnDestroy, AfterViewInit {
  public displayedColumns: string[] = [];
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<Appointment>;

  public isLoading = true;
  public dataSource: TeleconsultationDataSource;
  public selection = new SelectionModel<Appointment>(true, []);
  public allAppointements: Appointment[] = [];
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public has2FA = false;
  public skip2FA = false;
  public isAllServices = true;
  public minutesDelay = 15;
  public forceSelected = false;
  public filters: IFilter[] = [];
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public DATA_TYPE = DataType;
  public totalCount = 0;
  public FEATURES = FEATURES;
  public availableTeleconsultationPeriods: Item[] = [];
  public teleconsultationPeriodKey?: TELECONSULTATION_PERIOD_KEY = TELECONSULTATION_PERIOD_KEY.TODAY;
  public isTeleconsultationPeriodSelectorDisabled = false;
  public teleconsultationPeriodTooltip = "page.teleconsultations.periodSelect";
  public availableColumnItems = [
    {
      value: "start",
      display: "table.start",
      checked: true,
    },
    {
      value: "end",
      display: "table.duration",
      checked: true,
    },
    {
      value: "participant",
      display: "table.participantPractitioner",
      checked: true,
    },
    {
      value: "patient",
      display: "table.participantPatient",
      checked: true,
    },
    {
      value: "gender",
      display: "model.patient.gender",
      checked: true,
    },
    {
      value: "birthDate",
      display: "model.patient.birthdate",
      checked: true,
    },
    {
      value: "object",
      display: "table.description",
      checked: true,
    },
    {
      value: "dateSeenByPatient",
      display: "table.dateSeenByPatient",
      checked: true,
    },
    {
      value: "schedule",
      display: "forms.relatedPermissions.agenda",
      checked: true,
    },
    {
      value: "patientLastConnection",
      display: "table.patientLastConnection",
      checked: true,
    },
    {
      value: "practitionerLastConnection",
      display: "table.practitionerLastConnection",
      checked: true,
    },
  ];
  public actionOnMultiple = true;
  private refreshInterval;

  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();

  constructor(
    private appService: AppointmentService,
    private translateService: TranslateService,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private patientService: PatientService,
    private userService: UserService,
    private router: Router,
    public sessionService: SessionService,
    private healthcareService: HealthcareserviceService,
    public helpData: HelpData,
    private preferenceService: PreferenceService,
    private cdr: ChangeDetectorRef
  ) {}
  ngAfterViewInit(): void {
    this.skip2FA = this.sessionService.skip2FA;
    this.sessionService.refreshTeleconsultationsDataList.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.loadData();
    });

    if (
      this.sessionService.currentService &&
      this.userService.ownOrganization &&
      (this.healthcareService.availableServices()?.length > 0 || this.healthcareService.availableMonitoringServices().length > 0)
    ) {
      this.setupIsAllServices();
      this.setupServicesWatch();
      this.loadPreferences();
    } else {
      this.sessionService.servicesSetupWatch
        .pipe(
          skipWhile(() => !this.userService.ownOrganization || !this.sessionService.currentService),
          first()
        )
        .subscribe(() => {
          this.setupIsAllServices();
          this.setupServicesWatch();
          this.loadPreferences();
        });
    }
    // reset the paginator after sorting
    this.sort.sortChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => (this.paginator.pageIndex = 0));

    //  watch paginator and sort
    merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadData();
          this.updatePreference();
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();

    this.refreshInterval = setInterval(() => {
      this.loadData();
    }, 5 * 1000);
  }

  ngOnInit(): void {
    this.dataSource = new TeleconsultationDataSource(this.appService);
    this.setupTeleconsultationPeriods();

    this.userService
      .account()
      .pipe(
        takeUntil(this.onDestroy$),
        filter((a) => a !== null && a !== undefined)
      )
      .subscribe((account) => {
        this.has2FA = account.mfaActive;
      });
    this.dataSource.rawData$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      this.allAppointements = data;
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setupServicesWatch() {
    // This is a special case because, when its a monitoring user, we cannot
    // allow creation of teleconsultation when EITHER the normal services OR the
    // monitoring services are in "all" mode
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.setupIsAllServices();
      this.loadData();
    });
    if (this.userService.isMonitoringUser) {
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupIsAllServices();
        this.loadData();
      });
    }
  }

  private setupIsAllServices(): void {
    if (!this.sessionService.currentService || (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)) {
      this.isAllServices = true;
    }
    if (this.userService.isMonitoringUser) {
      this.isAllServices = this.sessionService.isAllServices(true) || this.sessionService.isAllServices(false);
    } else {
      this.isAllServices = this.sessionService.isAllServices(false);
    }
  }

  private async loadData(): Promise<void> {
    const services = this.isAllServices
      ? this.healthcareService.availableServices().map((s) => s.asReference.reference)
      : [this.sessionService.currentService?.reference];
    const filters = this.getFiltersFormated();
    const params = {
      filters: filters,
      sortId: this.sort?.active,
      sortOrder: this.sort?.direction,
      pageNumber: this.paginator?.pageIndex,
      pageSize: this.paginator?.pageSize,
      services: services,
      organization: this.sessionService.organization?.reference,
      appointmentPeriod: this.teleconsultationPeriodKey,
    };

    await this.dataSource.loadData(params);

    this.appService
      .getTeleconsultationsCount(filters, services, this.sessionService.organization?.reference, this.teleconsultationPeriodKey)
      .subscribe((res) => {
        this.totalCount = res.count;
      });
    const selectedIds = this.selection.selected.map((s) => s._id);

    this.selection.clear();

    this.dataSource.getData()?.forEach((row) => {
      if (selectedIds.includes(row._id)) {
        this.selection.select(row);
      }
    });
  }

  public applyFilter(filter: IFilter): void {
    this.forceSelected = false;
    this.dataSource.setFilter(filter);
    this.updateTeleconsultationPeriodSelectorState();
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.updatePreference();
    this.loadData();
  }

  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public delete(app: Appointment): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          message: this.translateService.instant("common.removeForEverAsk"),
          type: ConfirmationDialogType.CONFIRM,
        },
      })
      .afterClosed()
      .subscribe((yes) => {
        if (yes) {
          this.appService.deleteApp(app).subscribe(() => {
            this.loadData();
            const msg = this.translateService.instant("common.success");
            this.snackbar.open(msg, "ok", { duration: 3000 });
          });
        }
      });
  }

  public onStartVideoCall(appointment: Appointment): void {
    if (!this.has2FA && !this.skip2FA) {
      return;
    }
    this.appService.startVideoCall(appointment);
  }

  public async update(app: Appointment): Promise<void> {
    let patientUser = null;
    if (app.isNotUnknowPatient()) {
      const patientId = app.participantPatientRef.reference;
      patientUser = await this.patientService.getPatientUser(patientId).toPromise();
    }
    this.dialog.open(PatientTeleconsultationDialogComponent, {
      data: {
        appointment: app,
        patientUser,
        mode: FORMS_MODE.UPDATE,
        fromPatientWidget: app.isNotUnknowPatient(),
      },
    });
  }

  /**
   * Show confirmation modal before, change smsMailSent boolean and smsType
   */
  public resendSmsMail(app: Appointment): void {
    this.appService
      .resendMessageVisioAppointement(app)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.snackbar.open(this.translateService.instant("common.success"), undefined, {
          duration: 3000,
        });
      });
  }

  public goToPatient(app: Appointment): void {
    if (app.isNotUnknowPatient()) {
      const patientId = app.participantPatientRef.reference;
      const b64Id = Base64Helper.utf8_to_b64(patientId);
      this.router.navigateByUrl(`/patient;id=${b64Id}`, {
        state: undefined,
      });
    } else {
      return;
    }
  }

  public openVideoCallHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.teleconsultationsPageHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public masterToggle(): void {
    this.dataSource
      .connect(this.table)
      .pipe(first())
      .subscribe((rows) => {
        if (this.selection.hasValue() || this.forceSelected) {
          this.selection.clear();
          this.forceSelected = false;
        } else {
          this.selection.select(...rows);
        }
      });
  }

  public updateAppointmentsWithDelay(): void {
    if (this.selection.selected.length) {
      const appUpdates: Observable<IAppointment>[] = [];
      this.selection.selected.forEach((appointment) => {
        appointment.minutesDelay = this.minutesDelay;
        appUpdates.push(this.appService.updateVisioAppointement(appointment));
      });

      forkJoin(appUpdates).subscribe(
        () => {
          this.snackbar.open(`✅ ${this.translateService.instant("common.success")}`, undefined, {
            duration: 3000,
          });
        },
        (err) => {
          FileLogger.error("TeleconsultationsPageComponent", "Error while updating appointments: ", err);
        }
      );
    }
  }

  private loadPreferences() {
    this.preferenceService
      .list(PreferenceContext.TELECONSULTATIONS_PAGE)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((parameters: ITableParameter & { teleconsultationPeriodKey?: TELECONSULTATION_PERIOD_KEY }) => {
        this.setupTeleconsultationPeriods(parameters);
        if (parameters?.filters?.length) {
          // Apply saved filters
          parameters.filters.forEach((filter: IFilter) => {
            this.applyFilter(filter);
          });
        }
        // Set page size from preference
        this.paginator.pageSize = parameters?.itemsPerPage ?? 5;
        this.setupColumns(parameters);
        this.cdr.detectChanges();
        this.loadData().finally(() => {
          this.isLoading = false;
        });
      });
  }
  private setupColumns(preferences?: ITableParameter) {
    if (preferences?.availableColumnItems) {
      const savedColumns = preferences.availableColumnItems.filter((v) => v);

      // Merge : keep order and visibility + add new columns if needed
      const merged = [
        ...savedColumns,
        ...this.availableColumnItems.filter((col) => !savedColumns.some((saved) => saved.value === col.value)),
      ];

      this.availableColumnItems = merged;
    }

    this.setDisplayedColumns();
  }

  private updatePreference() {
    this.preferenceService
      .update({
        context: PreferenceContext.TELECONSULTATIONS_PAGE,
        parameters: {
          filters: this.dataSource?.getAllFilters(),
          itemsPerPage: this.paginator.pageSize,
          availableColumnItems: this.availableColumnItems,
          teleconsultationPeriodKey: this.teleconsultationPeriodKey,
        } as ITableParameter & { teleconsultationPeriodKey?: TELECONSULTATION_PERIOD_KEY },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.filters = this.dataSource?.getAllFilters();
      });
  }

  private getFiltersFormated(): IFilter[] {
    const formatedFilters: IFilter[] = [];
    const filters = Tools.deepCopy(this.dataSource.getAllFilters());
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        switch (filter.dataType) {
          case this.DATA_TYPE.DATE:
            // format date params to string
            filter.data.fromDate = moment(filter.data.fromDate).format("YYYY-MM-DD");
            filter.data.toDate = moment(filter.data.toDate).format("YYYY-MM-DD");
            break;
          case this.DATA_TYPE.CHOICE: {
            const values: Array<string> = [];
            const choices: Choice[] = filter.data ? filter.data : [];
            choices.forEach((choice) => {
              if (choice.checked) {
                values.push(choice.value);
              }
            });
            filter.data = {
              value: values,
            };
            break;
          }
        }
        formatedFilters.push(filter);
      });
    }
    this.filters = formatedFilters;
    return formatedFilters;
  }

  public onAutoAssignAppointments(): void {
    if (this.selection.selected.length) {
      this.appService.autoAssignAppointments(this.selection.selected).subscribe(
        () => {
          this.selection.clear();
          this.loadData();
          this.snackbar.open(`✅ ${this.translateService.instant("common.success")}`, undefined, {
            duration: 3000,
          });
        },
        (err) => {
          FileLogger.error("TeleconsultationsPageComponent", "Error while auto assign appointments: ", err);
        }
      );
    }
  }

  /**
   * Drag n'Drop
   */
  public async columnDropped(event: CdkDragDrop<string[]>): Promise<void> {
    const realIndexFrom =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.previousIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    const realIndexTo =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.currentIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.setDisplayedColumns();
    this.updatePreference();
  }

  public setDisplayedColumns(): void {
    this.displayedColumns = this.actionOnMultiple
      ? ["select", ...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value), "action"]
      : [...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value), "action"];
  }

  public async availableColumnChanged(_items: Item[]): Promise<void> {
    this.setDisplayedColumns();
    this.updatePreference();
  }

  public availableTeleconsultationPeriodsChanged(items: Item[]): void {
    if (this.isTeleconsultationPeriodSelectorDisabled) {
      return;
    }
    this.teleconsultationPeriodKey = items.find((i) => i.checked)?.value as TELECONSULTATION_PERIOD_KEY;
    this.selection.clear();
    this.forceSelected = false;
    this.paginator.pageIndex = 0;
    this.updatePreference();
    this.loadData();
  }

  public activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
    if (this.actionOnMultiple) {
      this.displayedColumns.unshift("select");
    } else {
      this.selection.clear();
      this.forceSelected = false;
      if (this.displayedColumns[0] === "select") {
        this.displayedColumns.shift();
      }
    }
  }

  private setupTeleconsultationPeriods(preferences?: ITableParameter & { teleconsultationPeriodKey?: TELECONSULTATION_PERIOD_KEY }): void {
    this.teleconsultationPeriodKey = preferences?.teleconsultationPeriodKey ?? TELECONSULTATION_PERIOD_KEY.TODAY;
    this.updateTeleconsultationPeriodSelectorState();
  }

  private updateTeleconsultationPeriodSelectorState(): void {
    this.isTeleconsultationPeriodSelectorDisabled = this.hasStartDateFilter();
    this.teleconsultationPeriodTooltip = this.isTeleconsultationPeriodSelectorDisabled
      ? "page.teleconsultations.periodDisabledStartFilterTooltip"
      : "page.teleconsultations.periodSelect";
    if (this.isTeleconsultationPeriodSelectorDisabled) {
      this.teleconsultationPeriodKey = undefined;
    } else if (!this.teleconsultationPeriodKey) {
      this.teleconsultationPeriodKey = TELECONSULTATION_PERIOD_KEY.TODAY;
    }
    this.availableTeleconsultationPeriods = [
      {
        value: TELECONSULTATION_PERIOD_KEY.TODAY,
        display: "teleconsultationPeriod.TODAY",
        checked: this.teleconsultationPeriodKey === TELECONSULTATION_PERIOD_KEY.TODAY,
      },
      {
        value: TELECONSULTATION_PERIOD_KEY.TODAY_TO_THREE_MONTHS,
        display: "teleconsultationPeriod.TODAY_TO_THREE_MONTHS",
        checked: this.teleconsultationPeriodKey === TELECONSULTATION_PERIOD_KEY.TODAY_TO_THREE_MONTHS,
      },
      {
        value: TELECONSULTATION_PERIOD_KEY.THREE_MONTHS_AGO_TO_TODAY,
        display: "teleconsultationPeriod.THREE_MONTHS_AGO_TO_TODAY",
        checked: this.teleconsultationPeriodKey === TELECONSULTATION_PERIOD_KEY.THREE_MONTHS_AGO_TO_TODAY,
      },
    ];
  }

  private hasStartDateFilter(): boolean {
    return this.dataSource?.getAllFilters().some((filter) => filter.propertyName === "start") ?? false;
  }
}
